import { Link } from '@inertiajs/react'
import InvestorPortalLayout from '@/layouts/InvestorPortalLayout'

interface Stats {
  total_committed: number
  ytd_distributions: number
  lifetime_distributions: number
  active_loan_count: number
  total_participations: number
}

interface Props {
  stats: Stats
  pending_offerings_count: number
  lender_names: string[]
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function Dashboard({ stats, pending_offerings_count, lender_names }: Props) {
  const currentYear = new Date().getFullYear()

  return (
    <InvestorPortalLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
        <p className="text-sm text-gray-500 mt-1">
          A summary of your loan participations
          {lender_names.length > 0 && (
            <> with {lender_names.join(', ')}</>
          )}
          .
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Capital Committed" value={fmtMoney(stats.total_committed)} />
        <StatCard label={`${currentYear} Distributions`} value={fmtMoney(stats.ytd_distributions)} highlight />
        <StatCard label="Lifetime Distributed" value={fmtMoney(stats.lifetime_distributions)} />
        <StatCard label="Active Loans" value={stats.active_loan_count.toString()} />
      </div>

      {pending_offerings_count > 0 && (
        <Link
          href="/investor-portal/offerings"
          className="mb-8 block bg-emerald-50 rounded-xl border border-emerald-200 p-5 hover:border-emerald-400 hover:shadow-sm transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-bold text-lg">
                {pending_offerings_count}
              </span>
              <div>
                <h2 className="text-base font-semibold text-emerald-900">
                  Pending Offering{pending_offerings_count === 1 ? '' : 's'}
                </h2>
                <p className="text-sm text-emerald-700">
                  Your lender has proposed new loan participation{pending_offerings_count === 1 ? '' : 's'} for your review.
                </p>
              </div>
            </div>
            <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Link
          href="/investor-portal/loans"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-emerald-300 hover:shadow-sm transition-all"
        >
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">My Loans</h2>
              <p className="text-sm text-gray-500 mt-1">
                {stats.total_participations} participation{stats.total_participations === 1 ? '' : 's'} —
                ownership %, principal share, distributions to date.
              </p>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>

        <Link
          href="/investor-portal/statements"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-emerald-300 hover:shadow-sm transition-all"
        >
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Year-End Statements</h2>
              <p className="text-sm text-gray-500 mt-1">
                Download annual statements your lender prepared for tax filing.
              </p>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      </div>

      <div className="mt-8 rounded-lg bg-amber-50 border border-amber-100 px-4 py-3">
        <p className="text-xs text-amber-700">
          Informational only. LendSolo is a tracking tool, not a tax advisor. Consult your accountant for tax filings.
        </p>
      </div>
    </InvestorPortalLayout>
  )
}

function StatCard({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
      <p className={`text-xs ${highlight ? 'text-emerald-700' : 'text-gray-500'} mb-1`}>{label}</p>
      <p className="text-lg font-bold font-mono text-gray-900">{value}</p>
    </div>
  )
}
